// Filter + sort + shuffle logic for the Provider Finder.
// All client-side — operates on the in-memory list loaded from providers.json.

/**
 * Calculates distance between two lat/lng points in miles using the
 * equirectangular projection — accurate at the scale of a single state / metro.
 */
export function distanceMiles(a, b) {
  if (!a || !b) return Infinity;
  const earthCircumferenceMiles = 24901;
  const ky = earthCircumferenceMiles / 360;
  const kx = Math.cos((Math.PI * a.lat) / 180) * ky;
  const dx = Math.abs(a.lng - b.lng) * kx;
  const dy = Math.abs(a.lat - b.lat) * ky;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Fisher-Yates shuffle. Returns a new array — does NOT mutate input.
 */
export function shuffle(array) {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Groups provider records by store_name. Each output entry is one practice
 * with the device list collapsed onto it.
 */
export function groupByStore(records) {
  const groups = new Map();
  for (const r of records) {
    if (!groups.has(r.store_name)) {
      groups.set(r.store_name, {
        store_name: r.store_name,
        address: r.address,
        lat: r.lat,
        lng: r.lng,
        phone: r.phone,
        web: r.web,
        is_skinclusive: r.is_skinclusive,
        devices: [],
      });
    }
    const group = groups.get(r.store_name);
    if (!group.devices.includes(r.device_name)) {
      group.devices.push(r.device_name);
    }
    // OR — if any record for this store is Skinclusive, treat the store as Skinclusive
    if (r.is_skinclusive) group.is_skinclusive = true;
  }
  return [...groups.values()];
}

/**
 * Applies the full filter pipeline to the providers list:
 *   1. Filter by selected category (asset_type matches category's FSM asset type values)
 *   2. Filter by distance from center
 *   3. Group by store_name (collapse multi-device practices)
 *   4. Shuffle (randomize order on every search — fixes the legacy bundle's
 *      initial-render-is-deterministic gap)
 *
 * Returns the full filtered+shuffled list. The caller decides how many to
 * display (typically first 5).
 *
 * @param {Array}  providers   The full list from providers.json
 * @param {Object} category    Selected category { name, fsm_asset_types[] }
 * @param {Object} center      { lat, lng } map center — usually the geocoded ZIP
 * @param {Number} radiusMiles How far out to search
 */
export function applyFilters(providers, category, center, radiusMiles) {
  if (!center) return [];

  // 1. Optional category filter — only apply if a device was selected. When no
  //    category is picked, ALL providers are eligible regardless of device.
  let candidates = providers;
  if (category) {
    const assetTypeSet = new Set(category.fsm_asset_types.map(t => t.toLowerCase()));
    candidates = candidates.filter(p =>
      assetTypeSet.has((p.device_name || '').toLowerCase())
    );
  }

  // 2. Distance filter
  const inRange = candidates.filter(p => {
    const d = distanceMiles({ lat: p.lat, lng: p.lng }, center);
    return d <= radiusMiles;
  });

  // 3. Group by store
  const grouped = groupByStore(inRange);

  // Attach computed distance for display + sorting fallback
  for (const g of grouped) {
    g.distance = distanceMiles({ lat: g.lat, lng: g.lng }, center);
  }

  // 4. Shuffle
  return shuffle(grouped);
}
